import { useCallback, useEffect, useMemo, useState } from "react";
import { OverlayTrigger } from "react-bootstrap";
import { Placement } from "react-bootstrap/esm/types";
import Popover from "react-bootstrap/Popover";

import classNames from "classnames";
import { isUndefined } from "lodash";
import { twMerge } from "tailwind-merge";

import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faCircle } from "@fortawesome/free-solid-svg-icons/faCircle";

import { SKSpinner } from "components/customElements/skSpinner";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import Input from "components/Input";

interface Option {
  value: any;
  label: string;
}

interface Props {
  label: string;
  options: Option[];
  selected?: any;
  multiple?: boolean;
  footer?: any;
  onSelect?: (_option: any) => void;
  filters?: any;
  filterKey?: string;
  containerClassName?: string;
  activeClassName?: string;
  optionContainerClassName?: string;
  optionListContainerClassName?: string;
  enableSearch?: boolean;
  className?: string;
  caretClassName?: string;
  placement?: Placement;
  showSelectionCount?: boolean;
  showSelectionCountZero?: boolean;
  loadingList?: boolean;
  emptyMessage?: any;
}

export default function Select({
  label,
  options,
  selected,
  multiple,
  onSelect,
  footer,
  filters,
  filterKey,
  className,
  activeClassName,
  containerClassName,
  optionContainerClassName,
  optionListContainerClassName,
  enableSearch = true,
  caretClassName,
  placement,
  showSelectionCount = true,
  showSelectionCountZero = true,
  loadingList,
  emptyMessage,
}: Props) {
  const [open, setOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<any>([]);
  const [filteredOptions, setFilteredOptions] = useState<any>(options);

  useEffect(() => {
    setSelectedOption(selected);
  }, [selected]);

  const isSelected = useCallback(
    (option) => {
      if (multiple) {
        return selectedOption?.find((item: any) => option.value === item.value);
      }

      return selectedOption?.value === option.value;
    },
    [multiple, selectedOption]
  );

  const onSelectOption = useCallback(
    (option: any) => {
      let newSelected = option;
      if (multiple) {
        if (isSelected(option)) {
          newSelected = selectedOption?.filter(
            (item: any) => item.value !== option.value
          );
        } else {
          newSelected = [...selectedOption, option];
        }
      }

      setSelectedOption(newSelected);
      onSelect?.(newSelected);
    },
    [isSelected, multiple, onSelect, selectedOption]
  );

  const onSelectOnly = useCallback(
    (option: any) => {
      const newSelected = [option];
      setSelectedOption(newSelected);
      onSelect?.(newSelected);
    },
    [onSelect]
  );

  const onSelectAll = useCallback(() => {
    if (selectedOption?.length === options.length) {
      setSelectedOption([]);
      onSelect?.([]);
    } else {
      setSelectedOption(options);
      onSelect?.(options);
    }
  }, [onSelect, options, selectedOption?.length]);

  const isAllSelected = useMemo(() => {
    if (multiple) {
      return selectedOption?.length === options.length;
    }
  }, [multiple, options.length, selectedOption?.length]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    if (filters && filterKey && isUndefined(filters[filterKey]) && multiple) {
      setSelectedOption(selected);
    }
  }, [filterKey, filters, multiple, selected]);

  const renderFilterOption = useCallback(
    (option: Option) => {
      const selected = isSelected(option);
      return (
        <div
          key={option.value}
          className="flex items-center justify-between group md:hover:bg-yellow-100 cursor-pointer"
        >
          <div
            className="flex items-center flex-1 pl-[10px] py-[10px] max-w-[80%]"
            onClick={() => onSelectOption(option)}
          >
            <div
              className={classNames(
                "flex mr-[10px] items-center justify-center rounded min-w-[20px] min-h-[20px]",
                selected && "bg-smart-teal-500 ",
                !selected && "border-[2px] border-solid border-slate-400",
                !multiple && "!rounded-full"
              )}
            >
              {selected && (
                <FontAwesomeIcon
                  icon={multiple ? faCheck : faCircle}
                  color="white"
                  className="text-[10px]"
                />
              )}
            </div>
            <span className="truncate">{option.label}</span>
          </div>
          {multiple && (
            <span
              onClick={() => onSelectOnly(option)}
              className="underline md:hidden mr-[10px] md:group-hover:block text-[10px] p-[2px] px-[5px] bg-black text-white"
            >
              Only
            </span>
          )}
        </div>
      );
    },
    [isSelected, multiple, onSelectOnly, onSelectOption]
  );

  const emptyOptions = useMemo(() => {
    if (emptyMessage) return emptyMessage;

    return (
      <div className="flex p-[16px]">
        <span>No Data Available</span>
      </div>
    );
  }, [emptyMessage]);

  return (
    <div
      className={twMerge("w-[200px] relative select-none", containerClassName)}
    >
      <OverlayTrigger
        placement={placement || "bottom"}
        trigger={"click"}
        rootClose
        show={open}
        onExit={() => {
          setOpen(false);
        }}
        onToggle={(data) => {
          if (!data) {
            setOpen(data);
          }
        }}
        overlay={
          <Popover
            className={twMerge(
              "overflow-hidden select-none w-[200px]",
              optionContainerClassName
            )}
          >
            <Popover.Body
              className={twMerge(
                "!p-0 max-h-[300px] overflow-y-scroll",
                optionListContainerClassName
              )}
            >
              {multiple && (
                <div className="">
                  {enableSearch && (
                    <div className="flex w-[100%] items-center p-[10px]">
                      <Input
                        onChange={(e) => {
                          const newOptions = options.filter((item) => {
                            const label = item.label.toLowerCase();
                            const search = e.target.value.toLowerCase();
                            return label.includes(search);
                          });
                          setFilteredOptions(newOptions);
                        }}
                        inputClassName="w-[100%]"
                        placeholder={`Search ${label}`}
                        className="w-[100%]"
                      />
                    </div>
                  )}
                  <div className="flex items-center p-[10px] bg-inactive-gray">
                    <div
                      className={classNames(
                        "flex mr-[10px] items-center justify-center rounded min-w-[20px] min-h-[20px] cursor-pointer",
                        isAllSelected && "bg-smart-teal-500 ",
                        !isAllSelected &&
                          "border-[2px] border-solid border-slate-400"
                      )}
                      onClick={onSelectAll}
                    >
                      {isAllSelected && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="white"
                          className="text-[10px]"
                        />
                      )}
                    </div>
                    {selectedOption?.length} Selected
                  </div>
                </div>
              )}
              {loadingList && (
                <div className="text-center my-[12px] text-inactive-gray">
                  <SKSpinner />
                </div>
              )}
              {filteredOptions.map(renderFilterOption)}
              {filteredOptions.length < 1 && !loadingList && emptyOptions}
              {selectedOption?.value === "custom" && footer}
            </Popover.Body>
          </Popover>
        }
      >
        <div
          className={twMerge(
            "p-[10px] text-[12px] shadow-md rounded flex items-center justify-between cursor-pointer",
            className,
            open && activeClassName
          )}
          onClick={() => setOpen(!open)}
        >
          <label className="truncate cursor-pointer">
            {label}{" "}
            {multiple
              ? showSelectionCount &&
                (showSelectionCountZero || selectedOption?.length > 0) &&
                `(${selectedOption?.length || "0"} Selected)`
              : selectedOption?.valueLabel && `(${selectedOption?.valueLabel})`}
          </label>
          <FontAwesomeIcon
            icon={faCaretDown}
            className={twMerge(
              "ml-[10px]",
              caretClassName,
              open && "transform rotate-180"
            )}
          />
        </div>
      </OverlayTrigger>
    </div>
  );
}
